import { useState, useEffect } from 'react';
import PageContent, { PageBreadcrumbProps } from '../PageContent';
import CTEyeService from '../../services/CTEyeService';
import { UserService } from '../../services';
import { Roles } from '../../models';
import { routes } from '../../app/routes';
import CTEyeRootSubmenu from './CTEyeRootSubmenu';
import { useNavigate } from 'react-router-dom';
import { GaugeConfig, UploadSessionModel } from '../../data/entities';
import { ActiveUploadsSection, InspectionHistorySection } from './components';
import { POLL_ACTIVE_SESSION_MS } from './helper/pollingConfig';

export function CTEyeUploadSessions() {
  const navigate = useNavigate();
  const title = 'Imported Inspections';
  const breadcrumbs: PageBreadcrumbProps = {
    showHome: true,
    values: [
      { label: 'CTEye Clients', url: routes.cteye },
      { label: title },
    ],
  };
  const service = new CTEyeService();
  const submenus = <CTEyeRootSubmenu />;

  const isStaffOrAdmin = UserService.isInRole(Roles.admin) || UserService.isInRole(Roles.staff) || UserService.isInRole(Roles.dataAnalyst);
  const companyId = UserService.getCompanyId();
  const companyFilter = !isStaffOrAdmin && companyId ? ` and CTEyeClient/companyId eq ${companyId}` : '';

  const [activeSessions, setActiveSessions] = useState<UploadSessionModel[]>([]);
  const [historySessions, setHistorySessions] = useState<UploadSessionModel[]>([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [gaugeConfigMap, setGaugeConfigMap] = useState<Record<string, GaugeConfig>>({});

  const loadSessions = async () => {
    try {
      const [activeResponse, historyResponse] = await Promise.all([
        service.getUploadSessionsOData(
          `$filter=(status eq 'Queued' or status eq 'InProgress') and uploadType ne 'LiveView'${companyFilter}&$orderby=startDate desc&$expand=Inspection`
        ),
        service.getUploadSessionsOData(
          `$filter=(status eq 'Completed' or status eq 'Failed') and uploadType ne 'LiveView'${companyFilter}&$orderby=endDate desc&$expand=Inspection`
        ),
      ]);

      if (activeResponse?.value) setActiveSessions(activeResponse.value);
      if (historyResponse?.value) setHistorySessions(historyResponse.value);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoadingActive(false);
    }
  };

  useEffect(() => {
    loadSessions();
    const interval = setInterval(loadSessions, POLL_ACTIVE_SESSION_MS);
    return () => clearInterval(interval);
  }, []);

  // Load gauge configs for unique client IDs from active sessions
  useEffect(() => {
    const clientIds = [...new Set(activeSessions.map(s => s.ctEyeClientId))];
    const missingIds = clientIds.filter(id => !(id in gaugeConfigMap));
    if (missingIds.length === 0) return;

    const loadConfigs = async () => {
      const results = await Promise.allSettled(
        missingIds.map(cid => service.getClientGaugeConfig(cid))
      );
      const newConfigs: Record<string, GaugeConfig> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) {
          newConfigs[missingIds[i]] = r.value;
        }
      });
      if (Object.keys(newConfigs).length > 0) {
        setGaugeConfigMap(prev => ({ ...prev, ...newConfigs }));
      }
    };
    loadConfigs();
  }, [activeSessions]);

  const handleViewResults = (session: UploadSessionModel) => {
    navigate(`/cteye/clients/${session.ctEyeClientId}/inspections/${session.inspectionId}`);
  };

  return (
    <PageContent title={title} breadcrumbs={breadcrumbs} submenu={submenus}>
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10 col-xl-8">
          {/* Page Header */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">Imports In Progress Across All Devices</h5>
                <p className="text-muted mb-0 small">
                  Monitor all client uploads &bull; Auto-refreshes every 2s
                </p>
              </div>
            </div>
          </div>
          <ActiveUploadsSection
            activeSessions={activeSessions}
            loadingActive={loadingActive}
            gaugeConfigResolver={(s) => gaugeConfigMap[s.ctEyeClientId] ?? null}
          />
          <InspectionHistorySection
            historySessions={historySessions}
            onViewResults={handleViewResults}
          />
        </div>
      </div>
    </PageContent>
  );
}

export default CTEyeUploadSessions;